import { ArrowDown, ArrowUp, Globe, ImageUp, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { presignUpload, putToS3 } from '../../api/comms';
import { patchInbox } from '../../api/endpoints';
import type {
  Inbox,
  WebsiteChannel,
  WidgetHoursWindow,
  WidgetOfficeHours,
  WidgetPrechatField,
  WidgetStarter,
} from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { SaveBar } from '../../components/setup/SetupKit';
import { Spinner } from '../../components/Spinner';
import { Toggle } from '../../components/Toggle';
import { deriveBases } from '../../config/bases';
import { useTimeFormat } from '../../lib/tenantTime';
import type { ChatSection } from './botSetup';
import type { WebsiteInbox } from './BotInstall';

const HEX = /^#[0-9a-fA-F]{6}$/;
const MAX_STARTERS = 8;
const MAX_HIDDEN_PATHS = 50;
const MAX_WINDOWS = 4;

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
] as const;

type DayKey = (typeof DAYS)[number]['key'];

const PRECHAT_LABEL: Record<WidgetPrechatField['key'], string> = {
  name: 'Name',
  email: 'Email address',
  phone: 'Phone number',
};

const DEFAULT_WINDOW: WidgetHoursWindow = { start: '09:00', end: '17:00' };

/** Text inputs are strings all the way down; '' means "unset", never "empty". */
interface Draft {
  widgetColor: string;
  actionColor: string;
  logoS3Key: string;
  homeEnabled: boolean;
  welcomeTitle: string;
  welcomeTagline: string;
  conversationStarters: WidgetStarter[];
  onlineStatusText: string;
  offlineStatusText: string;
  offlineMessage: string;
  privacyNotice: string;
  privacyUrl: string;
  prechatEnabled: boolean;
  prechatMessage: string;
  prechatFields: WidgetPrechatField[];
  officeHours: WidgetOfficeHours;
  botOverridesHours: boolean;
  desktopEnabled: boolean;
  mobileEnabled: boolean;
  attachmentsEnabled: boolean;
  hiddenPaths: string;
  widgetPosition: 'left' | 'right';
  launcherType: 'corner' | 'sidebar' | 'hidden';
  launcherText: string;
  sidebarBgColor: string;
  sidebarTextColor: string;
}

const draftFrom = (channel: WebsiteChannel): Draft => ({
  widgetColor: channel.widgetColor || '#0E7C86',
  actionColor: channel.actionColor ?? '',
  logoS3Key: channel.logoS3Key ?? '',
  homeEnabled: channel.homeEnabled !== false,
  welcomeTitle: channel.welcomeTitle ?? '',
  welcomeTagline: channel.welcomeTagline ?? '',
  conversationStarters: channel.conversationStarters ?? [],
  onlineStatusText: channel.onlineStatusText ?? '',
  offlineStatusText: channel.offlineStatusText ?? '',
  offlineMessage: channel.offlineMessage ?? '',
  privacyNotice: channel.privacyNotice ?? '',
  privacyUrl: channel.privacyUrl ?? '',
  prechatEnabled: !!channel.prechatEnabled,
  prechatMessage: channel.prechatMessage ?? '',
  prechatFields: channel.prechatFields ?? [],
  officeHours: channel.officeHours ?? { enabled: false, timezone: null, days: {} },
  botOverridesHours: !!channel.botOverridesHours,
  desktopEnabled: channel.desktopEnabled !== false,
  mobileEnabled: channel.mobileEnabled !== false,
  attachmentsEnabled: channel.attachmentsEnabled !== false,
  hiddenPaths: (channel.hiddenPaths ?? []).join('\n'),
  widgetPosition: channel.widgetPosition ?? 'right',
  launcherType: channel.launcherType ?? 'corner',
  launcherText: channel.launcherText ?? '',
  sidebarBgColor: channel.sidebarBgColor ?? '',
  sidebarTextColor: channel.sidebarTextColor ?? '',
});

const lines = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

// ------------------------------------------------------------------ controls

function ColorField({
  label,
  hint,
  value,
  fallback,
  onChange,
  clearable = false,
}: {
  label: string;
  hint?: string;
  value: string;
  /** Shown in the swatch while `value` is blank — what the widget will use. */
  fallback: string;
  onChange: (next: string) => void;
  clearable?: boolean;
}) {
  const effective = HEX.test(value) ? value : fallback;
  const invalid = value !== '' && !HEX.test(value);
  return (
    <Field label={label} hint={hint} error={invalid ? 'Use a hex color like #0E7C86.' : null}>
      <div className="wa-color">
        <input
          type="color"
          value={effective}
          aria-label={`Pick ${label.toLowerCase()}`}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          className="input"
          value={value}
          placeholder={clearable ? `${fallback} (inherited)` : fallback}
          aria-invalid={invalid}
          onChange={(e) => onChange(e.target.value)}
        />
        {clearable && value !== '' && (
          <button type="button" className="btn btn-ghost" onClick={() => onChange('')} title="Reset">
            <X size={14} />
          </button>
        )}
      </div>
    </Field>
  );
}

function SectionTabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: readonly { id: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <nav className="wa-subtabs">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={value === tab.id ? 'is-on' : ''}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}

function StarterList({
  starters,
  onChange,
}: {
  starters: WidgetStarter[];
  onChange: (next: WidgetStarter[]) => void;
}) {
  const patch = (index: number, fields: Partial<WidgetStarter>) =>
    onChange(starters.map((entry, i) => (i === index ? { ...entry, ...fields } : entry)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= starters.length) return;
    const next = [...starters];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <>
      {starters.map((starter, index) => (
        <div className="wa-starter" key={`starter-${index}`}>
          <div className="wa-starter-order">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={index === 0}
              onClick={() => move(index, -1)}
              title="Move up"
            >
              <ArrowUp size={13} />
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={index === starters.length - 1}
              onClick={() => move(index, 1)}
              title="Move down"
            >
              <ArrowDown size={13} />
            </button>
          </div>
          <Toggle
            checked={starter.enabled}
            onChange={(on) => patch(index, { enabled: on })}
            title=""
          />
          <div className="wa-starter-text">
            <input
              className="input"
              value={starter.label}
              maxLength={80}
              placeholder="Managing reservations"
              onChange={(e) => patch(index, { label: e.target.value })}
            />
            <input
              className="input wa-starter-prompt"
              value={starter.prompt ?? ''}
              maxLength={300}
              placeholder="What the visitor sends — blank sends the label itself"
              onChange={(e) => patch(index, { prompt: e.target.value })}
            />
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onChange(starters.filter((_, i) => i !== index))}
            title="Remove"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-secondary"
        disabled={starters.length >= MAX_STARTERS}
        onClick={() => onChange([...starters, { label: '', prompt: '', enabled: true }])}
      >
        <Plus size={14} />
        Add starter
      </button>
      {starters.length >= MAX_STARTERS && (
        <p className="field-hint">Starter limit reached ({MAX_STARTERS}).</p>
      )}
    </>
  );
}

function HoursEditor({
  hours,
  onChange,
}: {
  hours: WidgetOfficeHours;
  onChange: (next: WidgetOfficeHours) => void;
}) {
  const setDay = (day: DayKey, windows: WidgetHoursWindow[] | undefined) => {
    const days = { ...hours.days };
    if (!windows || !windows.length) delete days[day];
    else days[day] = windows;
    onChange({ ...hours, days });
  };

  return (
    <div className="wa-hours">
      {DAYS.map(({ key, label }) => {
        const windows = hours.days[key] ?? [];
        const open = windows.length > 0;
        return (
          <div className="wa-hours-row" key={key}>
            <Toggle
              checked={open}
              onChange={(on) => setDay(key, on ? [{ ...DEFAULT_WINDOW }] : [])}
              title={label}
            />
            <div className="wa-hours-windows">
              {!open && <span className="wa-hours-closed">Closed</span>}
              {windows.map((window, index) => (
                <div className="wa-hours-window" key={`${key}-${index}`}>
                  <input
                    className="input"
                    type="time"
                    value={window.start}
                    onChange={(e) =>
                      setDay(
                        key,
                        windows.map((w, i) => (i === index ? { ...w, start: e.target.value } : w)),
                      )
                    }
                  />
                  <span>to</span>
                  <input
                    className="input"
                    type="time"
                    value={window.end}
                    onChange={(e) =>
                      setDay(
                        key,
                        windows.map((w, i) => (i === index ? { ...w, end: e.target.value } : w)),
                      )
                    }
                  />
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setDay(key, windows.filter((_, i) => i !== index))}
                    title="Remove range"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
              {open && windows.length < MAX_WINDOWS && (
                <button
                  type="button"
                  className="linklike"
                  onClick={() => setDay(key, [...windows, { ...DEFAULT_WINDOW }])}
                >
                  Add range
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------- preview

const PREVIEW_SCREENS = [
  { id: 'home', label: 'Home' },
  { id: 'chat', label: 'Chat' },
  { id: 'closed', label: 'Launcher' },
] as const;

type PreviewScreen = (typeof PREVIEW_SCREENS)[number]['id'];

/**
 * The REAL widget frame, driven entirely by postMessage — no session, no
 * network, no contact. It has to be the real one: a hand-built mock of a
 * surface this configurable drifts from it within a release, and the operator
 * would be tuning a picture of the widget rather than the widget.
 */
function WidgetPreview({
  draft,
  logoUrl,
  screen,
  onScreen,
}: {
  draft: Draft;
  logoUrl: string | null;
  screen: PreviewScreen;
  onScreen: (next: PreviewScreen) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = useState(false);
  const [size, setSize] = useState({ width: 380, height: 600, open: true });
  const origin = window.location.origin;

  const config = useMemo(
    () => ({
      color: HEX.test(draft.widgetColor) ? draft.widgetColor : '#0E7C86',
      actionColor: HEX.test(draft.actionColor) ? draft.actionColor : draft.widgetColor,
      position: draft.widgetPosition,
      launcherType: draft.launcherType,
      launcherText: draft.launcherText || null,
      sidebarBgColor: HEX.test(draft.sidebarBgColor) ? draft.sidebarBgColor : draft.widgetColor,
      sidebarTextColor: HEX.test(draft.sidebarTextColor) ? draft.sidebarTextColor : '#FFFFFF',
      logoUrl,
      homeEnabled: draft.homeEnabled,
      welcomeTitle: draft.welcomeTitle || null,
      welcomeTagline: draft.welcomeTagline || null,
      starters: draft.conversationStarters
        .filter((entry) => entry.enabled && entry.label.trim())
        .map((entry) => ({ label: entry.label, prompt: entry.prompt || entry.label })),
      // The preview shows the open state; the closed one is a click away in the
      // widget itself, and an operator writing offline copy wants to see it.
      online: true,
      statusText: draft.onlineStatusText || null,
      offlineMessage: null,
      privacyNotice: draft.privacyNotice || null,
      privacyUrl: draft.privacyUrl || null,
      prechatEnabled: draft.prechatEnabled,
      prechatMessage: draft.prechatMessage || null,
      prechatFields: draft.prechatFields,
      desktopEnabled: true,
      mobileEnabled: true,
      attachmentsEnabled: draft.attachmentsEnabled,
      hiddenPaths: [],
      identityEnabled: false,
    }),
    [draft, logoUrl],
  );

  const onMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { type?: string; width?: number; height?: number; open?: boolean };
      if (data?.type === 'sphere:ready') setReady(true);
      if (data?.type === 'sphere:size') {
        setSize({
          width: Number(data.width) || 0,
          height: Number(data.height) || 0,
          open: !!data.open,
        });
      }
    },
    [origin],
  );

  useEffect(() => {
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onMessage]);

  // Every keystroke repaints the frame. Cheap: it is a postMessage into an
  // already-mounted React tree, not a reload.
  useEffect(() => {
    if (!ready) return;
    frameRef.current?.contentWindow?.postMessage(
      { type: 'sphere:preview', config, screen },
      origin,
    );
  }, [ready, config, screen, origin]);

  // Mirrors the loader's own rule (console/public/widget.js): a closed sidebar
  // tab clings to the window edge with no gap, so the stage standing in for
  // that window must not hold it off one either.
  const sideTab = !size.open && draft.launcherType === 'sidebar';
  // With no launcher of ours there is genuinely nothing to draw closed, and an
  // empty stage reads as a broken preview rather than as the answer.
  const noLauncher = !size.open && draft.launcherType === 'hidden';

  return (
    <div className="wa-preview">
      <div className="wa-preview-head">
        <span className="manage-card-title">Preview</span>
        <SectionTabs tabs={PREVIEW_SCREENS} value={screen} onChange={onScreen} />
      </div>
      {noLauncher && (
        <p className="wa-stage-note">
          Nothing shows on the page until your own button opens it. Pick another screen to see
          the panel it opens.
        </p>
      )}
      <div className={sideTab ? `wa-stage is-side is-side-${draft.widgetPosition}` : 'wa-stage'}>
        <div
          className="wa-stage-frame"
          style={{
            width: size.width || 76,
            // The open frame is the panel PLUS the launcher docked under it, so
            // this ceiling has to clear both or the preview crops the very
            // control the operator is looking at.
            height: Math.min(size.height || 76, 660),
            alignSelf: draft.widgetPosition === 'left' ? 'flex-start' : 'flex-end',
          }}
        >
          <iframe
            ref={frameRef}
            title="Widget preview"
            src={`${origin}/widget.html?preview=1`}
            style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
          />
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- screen

const CONTENT_TABS = [
  { id: 'home', label: 'Home' },
  { id: 'chat', label: 'Chat' },
  { id: 'survey', label: 'Pre-chat survey' },
] as const;

type ContentTab = (typeof CONTENT_TABS)[number]['id'];

function AppearanceForm({
  inbox,
  onSaved,
  onDirty,
  onGo,
}: {
  inbox: WebsiteInbox;
  onSaved: (fresh: Inbox) => void;
  onDirty: (dirty: boolean) => void;
  onGo: (section: ChatSection) => void;
}) {
  const { zone } = useTimeFormat();
  // What the server last answered for this channel — the PATCH's own answer,
  // not the inbox list's later re-read, so the screen is not "unsaved" for a
  // beat after every save (the analytics forms' rule).
  const [base, setBase] = useState<WebsiteChannel>(inbox.channel);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(inbox.channel));
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(draftFrom(base)), [draft, base]);
  useEffect(() => onDirty(dirty), [onDirty, dirty]);
  const [contentTab, setContentTab] = useState<ContentTab>('home');
  const [screen, setScreen] = useState<PreviewScreen>('home');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [localLogo, setLocalLogo] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setSaved(false);
  };

  const crmBase = useMemo(
    () => deriveBases(window.location.hostname, import.meta.env as Record<string, string>).crmBase,
    [],
  );

  /**
   * A just-uploaded file shows from its local blob; a saved one comes back
   * through the widget's own public route, which is where a visitor gets it —
   * so the preview and the site cannot disagree about what the logo looks like.
   */
  const logoUrl =
    localLogo ??
    (draft.logoS3Key && draft.logoS3Key === base.logoS3Key
      ? `${crmBase}/widget/logo/${inbox.channel.websiteToken}`
      : null);

  const uploadLogo = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const { s3Key, putUrl } = await presignUpload(file.name, file.type || 'image/png');
      await putToS3(putUrl, file);
      set('logoS3Key', s3Key);
      setLocalLogo(URL.createObjectURL(file));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not upload that image.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const fresh = await patchInbox(inbox.id, {
        channel: {
          widgetColor: draft.widgetColor.trim(),
          actionColor: draft.actionColor.trim(),
          logoS3Key: draft.logoS3Key,
          homeEnabled: draft.homeEnabled,
          welcomeTitle: draft.welcomeTitle,
          welcomeTagline: draft.welcomeTagline,
          // Blank labels are dropped rather than saved — an empty row in the
          // editor is an unfinished thought, not a starter.
          conversationStarters: draft.conversationStarters
            .filter((entry) => entry.label.trim())
            .map((entry) => ({
              label: entry.label.trim(),
              prompt: (entry.prompt ?? '').trim(),
              enabled: entry.enabled,
            })),
          onlineStatusText: draft.onlineStatusText,
          offlineStatusText: draft.offlineStatusText,
          offlineMessage: draft.offlineMessage,
          privacyNotice: draft.privacyNotice,
          privacyUrl: draft.privacyUrl.trim(),
          prechatEnabled: draft.prechatEnabled,
          prechatMessage: draft.prechatMessage,
          prechatFields: draft.prechatFields,
          officeHours: draft.officeHours,
          botOverridesHours: draft.botOverridesHours,
          desktopEnabled: draft.desktopEnabled,
          mobileEnabled: draft.mobileEnabled,
          attachmentsEnabled: draft.attachmentsEnabled,
          hiddenPaths: lines(draft.hiddenPaths),
          widgetPosition: draft.widgetPosition,
          launcherType: draft.launcherType,
          launcherText: draft.launcherText,
          sidebarBgColor: draft.sidebarBgColor.trim(),
          sidebarTextColor: draft.sidebarTextColor.trim(),
        },
      });
      setSaved(true);
      setLocalLogo(null);
      if (fresh.channelType === 'website' && 'websiteToken' in fresh.channel) {
        setBase(fresh.channel);
        setDraft(draftFrom(fresh.channel));
      }
      onSaved(fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the widget.');
    } finally {
      setBusy(false);
    }
  };

  const toggleField = (key: WidgetPrechatField['key'], on: boolean) => {
    const without = draft.prechatFields.filter((field) => field.key !== key);
    set(
      'prechatFields',
      on ? [...without, { key, label: null, required: key !== 'phone' }] : without,
    );
  };
  const fieldFor = (key: WidgetPrechatField['key']) =>
    draft.prechatFields.find((field) => field.key === key);

  return (
    <div className="wa-layout">
      <div className="wa-form">
        {error && <ErrorNote error={error} />}

        <section className="card card-pad manage-card">
          <h2 className="manage-card-title">General</h2>
          <div className="bot-row">
            <ColorField
              label="Background color"
              hint="The panel header and the corner bubble."
              value={draft.widgetColor}
              fallback="#0E7C86"
              onChange={(v) => set('widgetColor', v)}
            />
            <ColorField
              label="Action color"
              hint="Buttons, links and the active tab. Blank follows the background."
              value={draft.actionColor}
              fallback={HEX.test(draft.widgetColor) ? draft.widgetColor : '#0E7C86'}
              clearable
              onChange={(v) => set('actionColor', v)}
            />
          </div>

          <Field label="Brand logo" hint="Shown on the Home screen and in the chat header. PNG or SVG, transparent background works best.">
            <div className="wa-logo">
              {logoUrl ? (
                <img className="wa-logo-preview" src={logoUrl} alt="" />
              ) : (
                <div className="wa-logo-empty">No logo</div>
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void uploadLogo(file);
                }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
              >
                {uploading ? <Spinner /> : <ImageUp size={14} />}
                {draft.logoS3Key ? 'Replace' : 'Upload'}
              </button>
              {draft.logoS3Key && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    set('logoS3Key', '');
                    setLocalLogo(null);
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </Field>
        </section>

        <section className="card card-pad manage-card">
          <h2 className="manage-card-title">Content</h2>
          <SectionTabs tabs={CONTENT_TABS} value={contentTab} onChange={setContentTab} />

          {contentTab === 'home' && (
            <>
              <Toggle
                checked={draft.homeEnabled}
                onChange={(on) => {
                  set('homeEnabled', on);
                  setScreen(on ? 'home' : 'chat');
                }}
                title="Home screen"
                sub="A welcome page with shortcuts, ahead of the conversation. Off opens the widget straight into the chat."
              />
              {draft.homeEnabled && (
                <>
                  <Field label="Header">
                    <input
                      className="input"
                      value={draft.welcomeTitle}
                      maxLength={120}
                      placeholder="Hi there 👋"
                      onChange={(e) => set('welcomeTitle', e.target.value)}
                    />
                  </Field>
                  <Field label="Message">
                    <textarea
                      className="textarea"
                      rows={2}
                      value={draft.welcomeTagline}
                      maxLength={500}
                      placeholder="Welcome to LimoSphere"
                      onChange={(e) => set('welcomeTagline', e.target.value)}
                    />
                  </Field>

                  <h3 className="bot-subhead">Conversation starters</h3>
                  <p className="manage-card-sub">
                    Shortcuts on the Home screen. Tapping one opens the chat with that question
                    already asked, so the assistant has something to answer.
                  </p>
                  <StarterList
                    starters={draft.conversationStarters}
                    onChange={(next) => set('conversationStarters', next)}
                  />
                </>
              )}

              <h3 className="bot-subhead">Status line</h3>
              <div className="bot-row">
                <Field label="When you're open" hint="Shown beside the chat shortcut.">
                  <input
                    className="input"
                    value={draft.onlineStatusText}
                    maxLength={120}
                    placeholder="We reply immediately"
                    onChange={(e) => set('onlineStatusText', e.target.value)}
                  />
                </Field>
                <Field label="When you're closed">
                  <input
                    className="input"
                    value={draft.offlineStatusText}
                    maxLength={120}
                    placeholder="We typically reply within a few minutes"
                    onChange={(e) => set('offlineStatusText', e.target.value)}
                  />
                </Field>
              </div>
            </>
          )}

          {contentTab === 'chat' && (
            <>
              <Field
                label="Offline message"
                hint="Shown inside the chat while you're outside office hours. Nothing is shown when you have no schedule."
              >
                <textarea
                  className="textarea"
                  rows={3}
                  value={draft.offlineMessage}
                  maxLength={1000}
                  placeholder="We're away right now. Leave your question and we'll answer as soon as we're back."
                  onChange={(e) => set('offlineMessage', e.target.value)}
                />
              </Field>
              <Field
                label="Privacy notice"
                hint="Shown under the composer until the visitor sends their first message."
              >
                <textarea
                  className="textarea"
                  rows={2}
                  value={draft.privacyNotice}
                  maxLength={500}
                  placeholder="By starting a chat you agree to how we handle your data."
                  onChange={(e) => set('privacyNotice', e.target.value)}
                />
              </Field>
              <Field label="Privacy policy link (optional)">
                <input
                  className="input"
                  value={draft.privacyUrl}
                  placeholder="https://www.example.com/privacy"
                  onChange={(e) => set('privacyUrl', e.target.value)}
                />
              </Field>
              <Toggle
                checked={draft.attachmentsEnabled}
                onChange={(on) => set('attachmentsEnabled', on)}
                title="Let visitors attach files"
                sub="A paperclip in the composer — photos, PDFs and documents up to 10 MB, five per message. They arrive on the conversation like any other attachment."
              />
            </>
          )}

          {contentTab === 'survey' && (
            <>
              <Toggle
                checked={draft.prechatEnabled}
                onChange={(on) => set('prechatEnabled', on)}
                title="Ask before the chat starts"
                sub="New visitors fill this in before their first message. Returning visitors never see it again."
              />
              {draft.prechatEnabled && (
                <>
                  <Field label="Intro">
                    <textarea
                      className="textarea"
                      rows={2}
                      value={draft.prechatMessage}
                      maxLength={500}
                      placeholder="Tell us how to reach you and we'll take it from there."
                      onChange={(e) => set('prechatMessage', e.target.value)}
                    />
                  </Field>
                  {(['name', 'email', 'phone'] as const).map((key) => {
                    const field = fieldFor(key);
                    return (
                      <div className="wa-survey-field" key={key}>
                        <Toggle
                          checked={!!field}
                          onChange={(on) => toggleField(key, on)}
                          title={PRECHAT_LABEL[key]}
                        />
                        {field && (
                          <>
                            <input
                              className="input"
                              value={field.label ?? ''}
                              maxLength={60}
                              placeholder={`Label — defaults to "${PRECHAT_LABEL[key]}"`}
                              onChange={(e) =>
                                set(
                                  'prechatFields',
                                  draft.prechatFields.map((f) =>
                                    f.key === key ? { ...f, label: e.target.value } : f,
                                  ),
                                )
                              }
                            />
                            <Toggle
                              checked={field.required}
                              onChange={(on) =>
                                set(
                                  'prechatFields',
                                  draft.prechatFields.map((f) =>
                                    f.key === key ? { ...f, required: on } : f,
                                  ),
                                )
                              }
                              title="Required"
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                  <Note tone="info">
                    An email or phone typed here is recorded as something the visitor{' '}
                    <em>claims</em>, not as verified contact details — it never merges them into an
                    existing customer, and the assistant still asks for a verification code before
                    it will discuss a reservation.
                  </Note>
                </>
              )}
            </>
          )}
        </section>

        <section className="card card-pad manage-card">
          <h2 className="manage-card-title">Office hours</h2>
          <p className="manage-card-sub">
            Decides which status line a visitor reads, and whether the offline message shows. The
            schedule runs on your clock, not the visitor's.
          </p>
          <Toggle
            checked={draft.officeHours.enabled}
            onChange={(on) =>
              set('officeHours', {
                ...draft.officeHours,
                enabled: on,
                timezone: draft.officeHours.timezone ?? zone ?? null,
                // Switching it on with nothing open would close the widget for
                // good, so a first enable starts on a normal working week.
                days: Object.keys(draft.officeHours.days).length
                  ? draft.officeHours.days
                  : {
                      mon: [{ ...DEFAULT_WINDOW }],
                      tue: [{ ...DEFAULT_WINDOW }],
                      wed: [{ ...DEFAULT_WINDOW }],
                      thu: [{ ...DEFAULT_WINDOW }],
                      fri: [{ ...DEFAULT_WINDOW }],
                    },
              })
            }
            title="Use a schedule"
            sub="Off means always open — visitors always read the open status line."
          />
          {draft.officeHours.enabled && (
            <>
              <Field
                label="Timezone"
                hint={
                  zone
                    ? `Your dispatch timezone is ${zone}.`
                    : 'An IANA name, e.g. America/New_York.'
                }
              >
                <input
                  className="input"
                  value={draft.officeHours.timezone ?? ''}
                  placeholder={zone ?? 'America/New_York'}
                  onChange={(e) =>
                    set('officeHours', {
                      ...draft.officeHours,
                      timezone: e.target.value.trim() || null,
                    })
                  }
                />
              </Field>
              <HoursEditor
                hours={draft.officeHours}
                onChange={(next) => set('officeHours', next)}
              />
              <Toggle
                checked={draft.botOverridesHours}
                onChange={(on) => set('botOverridesHours', on)}
                title="Count the assistant as open"
                sub="Outside these hours, still show the open status line whenever the chatbot is enabled on this inbox — because somebody really is answering."
              />
            </>
          )}
        </section>

        <section className="card card-pad manage-card">
          <h2 className="manage-card-title">Visibility and position</h2>

          <p className="manage-card-sub">
            Which sites may show the chat at all is under{' '}
            <button type="button" className="an-link" onClick={() => onGo('install')}>
              Install → Keep it to your own site
            </button>
            .
          </p>

          <Field
            label="Hide on these pages (one per line)"
            hint={`Paths, not full urls — /checkout hides one page, /account* hides everything below it. Up to ${MAX_HIDDEN_PATHS}.`}
          >
            <textarea
              className="textarea"
              rows={3}
              value={draft.hiddenPaths}
              placeholder="/checkout&#10;/account*"
              onChange={(e) => set('hiddenPaths', e.target.value)}
            />
          </Field>

          <h3 className="bot-subhead">Where it shows</h3>
          <Toggle
            checked={draft.desktopEnabled}
            onChange={(on) => set('desktopEnabled', on)}
            title="Desktop"
          />
          <Toggle
            checked={draft.mobileEnabled}
            onChange={(on) => set('mobileEnabled', on)}
            title="Mobile"
            sub="Screens under 768px wide."
          />
          {!draft.desktopEnabled && !draft.mobileEnabled && (
            <Note tone="warn">
              With both off the widget never appears anywhere. Conversations already open still
              reach you, but nobody new can start one.
            </Note>
          )}

          <h3 className="bot-subhead">Launcher</h3>
          <div className="bot-mode-switch">
            {(
              [
                {
                  value: 'corner' as const,
                  label: 'Corner bubble',
                  sub: 'A round button in the bottom corner, with an optional label beside it.',
                },
                {
                  value: 'sidebar' as const,
                  label: 'Sidebar tab',
                  sub: 'A labelled tab fixed to the middle of the page edge.',
                },
                {
                  value: 'hidden' as const,
                  label: 'Your own button',
                  sub: 'No launcher of ours. A button on your site opens it.',
                },
              ]
            ).map((option) => (
              <button
                key={option.value}
                type="button"
                className={`bot-mode ${draft.launcherType === option.value ? 'is-on' : ''}`}
                onClick={() => {
                  set('launcherType', option.value);
                  setScreen('closed');
                }}
              >
                <span className="bot-mode-label">{option.label}</span>
                <span className="bot-mode-sub">{option.sub}</span>
              </button>
            ))}
          </div>

          {draft.launcherType === 'hidden' && (
            <Note tone="info">
              Add <code>data-sphere-chat</code> to any button or link on your site and it opens
              the chat — <code>&lt;button data-sphere-chat&gt;Chat with us&lt;/button&gt;</code>.
              From your own code, <code>window.$sphereChat.open()</code> does the same. The
              snippet itself does not change.
            </Note>
          )}

          <div className="bot-row">
            <Field label="Side of the page">
              <div className="wa-radios">
                {(['left', 'right'] as const).map((side) => (
                  <label key={side} className="wa-radio">
                    <input
                      type="radio"
                      name="widgetPosition"
                      checked={draft.widgetPosition === side}
                      onChange={() => set('widgetPosition', side)}
                    />
                    {side === 'left' ? 'Left' : 'Right'}
                  </label>
                ))}
              </div>
            </Field>
            {/* Nothing of ours is on the page to label when the tenant's own
                button is the launcher — the side still matters, because it is
                where the panel opens. */}
            {draft.launcherType !== 'hidden' && (
              <Field
                label={draft.launcherType === 'sidebar' ? 'Tab label' : 'Label (optional)'}
                hint={
                  draft.launcherType === 'sidebar'
                    ? 'Blank falls back to the Home header.'
                    : 'Short text beside the bubble; blank shows the bubble alone.'
                }
              >
                <input
                  className="input"
                  value={draft.launcherText}
                  maxLength={60}
                  placeholder="Chat with us 24/7"
                  onChange={(e) => set('launcherText', e.target.value)}
                />
              </Field>
            )}
          </div>

          {draft.launcherType === 'sidebar' && (
            <div className="bot-row">
              <ColorField
                label="Tab background"
                value={draft.sidebarBgColor}
                fallback={HEX.test(draft.widgetColor) ? draft.widgetColor : '#0E7C86'}
                clearable
                onChange={(v) => set('sidebarBgColor', v)}
              />
              <ColorField
                label="Tab text"
                value={draft.sidebarTextColor}
                fallback="#FFFFFF"
                clearable
                onChange={(v) => set('sidebarTextColor', v)}
              />
            </div>
          )}
        </section>

        <SaveBar
          dirty={dirty}
          busy={busy || uploading}
          saved={saved}
          blocked={false}
          savedNote="Widget saved. Visitors see it on their next page load."
          onSave={() => void save()}
          onDiscard={() => {
            setDraft(draftFrom(base));
            setLocalLogo(null);
            setError(null);
          }}
        />
      </div>

      <WidgetPreview draft={draft} logoUrl={logoUrl} screen={screen} onScreen={setScreen} />
    </div>
  );
}

/**
 * How the widget looks and what it offers before anyone types — the half of the
 * chatbot a visitor meets first. It lives beside the assistant's instructions
 * rather than on the inbox, because "set up my chatbot" and "configure a
 * channel" are the same errand for whoever is doing this.
 *
 * The widget's look IS the channel's settings, so this is filled in for the
 * website chat the screen is on — chosen once at the top, for Install and here
 * alike — and only for one the reader manages: being the tenant's bot
 * administrator does not grant that, the two permissions are separate.
 */
export function ChatWidgetSection({
  inbox,
  theirs,
  onSaved,
  onDirty,
  onGo,
}: {
  inbox: WebsiteInbox | null;
  theirs: boolean;
  onSaved: (fresh: Inbox) => void;
  onDirty: (section: ChatSection, dirty: boolean) => void;
  onGo: (section: ChatSection) => void;
}) {
  const reportDirty = useCallback((dirty: boolean) => onDirty('widget', dirty), [onDirty]);
  // A form that unmounts is not holding anything.
  useEffect(() => () => onDirty('widget', false), [onDirty]);

  if (!inbox) {
    return (
      <section className="card card-pad manage-card">
        <h2 className="manage-card-title">
          {theirs ? 'Someone else manages the website chat' : 'No website chat yet'}
        </h2>
        <p className="manage-card-sub">
          {theirs
            ? 'The widget’s look belongs to the channel, and this one is not yours to configure — ask an administrator for Manage on it.'
            : 'The widget’s look belongs to a website chat. Create one on Install and its colours, Home screen and office hours all become editable here.'}
        </p>
        {theirs ? null : (
          <div className="bot-wizard-links">
            <button type="button" className="btn btn-primary" onClick={() => onGo('install')}>
              <Globe size={14} />
              Create a website chat
            </button>
          </div>
        )}
      </section>
    );
  }

  return <AppearanceForm key={inbox.id} inbox={inbox} onSaved={onSaved} onDirty={reportDirty} onGo={onGo} />;
}
